import { useClientOptions, useUser } from '@paintscout/ui-business'
import { getFeatures, hasIntegrationInterface } from '@paintscout/util/builder'
import type { QuoteDocument } from 'paintscout'
import { useWisetack } from '../Wisetack'

/**
 * Whether the Estimate Settings dialog has a Payments tab for this document. Every setting on that tab
 * belongs to Payrix, Stripe or Wisetack financing, so an account with only manual payments has nothing
 * to show. The dialog hides the tab and the Payments card hides its Settings button on the same rule
 * (ENG-1997). `showWisetackFinancing` is returned too, because the dialog's search index gates the
 * Wisetack Deposit Amount row on it alone.
 *
 * A template keeps only Surcharging: `createQuote` copies a template's `surcharge` onto the estimate
 * when `templateSurchargeOverride` is on, and drops every other payment option. Canadian Payrix has no
 * surcharging, so a template there has no payment settings at all.
 */
export function useHasPaymentSettings(quote?: QuoteDocument) {
  const { options } = useClientOptions()
  const { user, preferences } = useUser()
  const { hasWisetack } = useWisetack({ options, preferences, user })

  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments =
    hasIntegrationInterface({ preferences, options, integration: 'stripe' }) ||
    hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' }) ||
    hasPayrix

  if (quote?.type === 'quoteTemplate') {
    const isCanadianPayrix = hasPayrix && options.options.integrations?.payrix?.config?.country === 'Canada'
    const { templateSurchargeOverride } = getFeatures({ options })
    return {
      hasPaymentSettings: hasPayments && !isCanadianPayrix && !!templateSurchargeOverride,
      showWisetackFinancing: false
    }
  }

  const showWisetackFinancing =
    hasWisetack &&
    !quote?.is_invoice &&
    !!options.options.integrations?.wisetack?.config?.progressPaymentsEnabled &&
    !quote?.totals?.amount_paid &&
    !quote?.depositInfo

  return { hasPaymentSettings: hasPayments || showWisetackFinancing, showWisetackFinancing }
}
